import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';
import { on } from '@ember/modifier';
import { LinkTo } from '@ember/routing';
import { VStack } from 'tio-reimagined/components/layout';
import Chip from 'tio-reimagined/components/chip';
import Button from 'tio-reimagined/components/button';
import Heading from 'tio-reimagined/components/heading';
import Text from 'tio-reimagined/components/text';
import PageHero from 'tio-reimagined/components/page-hero';
import SectionHeader from 'tio-reimagined/components/section-header';
import Input from 'tio-reimagined/components/input';
import Textarea from 'tio-reimagined/components/textarea';
import Checkbox from 'tio-reimagined/components/checkbox';
import CheckboxGroup from 'tio-reimagined/components/checkbox-group';
import RadioGroup from 'tio-reimagined/components/radio-group';
import type { RadioOption } from 'tio-reimagined/components/radio-group';
import Switch from 'tio-reimagined/components/switch';
import SearchInput from 'tio-reimagined/components/search-input';
import Form from 'tio-reimagined/components/form';
import * as v from 'valibot';
import {
  PhArrowLeft,
  PhTextbox,
  PhTextAa,
  PhCheckSquare,
  PhRadioButton,
  PhToggleLeft,
  PhMagnifyingGlass,
  PhClipboardText,
  PhEnvelope,
  PhUser,
  PhLock,
  PhPhone,
} from 'ember-phosphor-icons';

// ---------------------------------------------------------------------------
// Mock data
// ---------------------------------------------------------------------------

const FREQUENCY_OPTIONS: RadioOption[] = [
  {
    key: 'daily',
    label: 'Daily',
    description: 'Receive notifications every day',
    dataTestId: 'radio-daily',
  },
  {
    key: 'weekly',
    label: 'Weekly',
    description: 'Digest sent every Monday',
    dataTestId: 'radio-weekly',
  },
  {
    key: 'monthly',
    label: 'Monthly',
    description: 'Summary at the end of each month',
    dataTestId: 'radio-monthly',
  },
  {
    key: 'never',
    label: 'Never',
    description: 'Disable all notifications',
    disabled: true,
    dataTestId: 'radio-never',
  },
];

const PRIORITY_OPTIONS: RadioOption[] = [
  { key: 'low', label: 'Low', dataTestId: 'radio-low' },
  { key: 'medium', label: 'Medium', dataTestId: 'radio-medium' },
  { key: 'high', label: 'High', dataTestId: 'radio-high' },
  { key: 'critical', label: 'Critical', dataTestId: 'radio-critical' },
];

// ---------------------------------------------------------------------------
// Valibot schema for Form demo
// ---------------------------------------------------------------------------

const contactSchema = v.object({
  firstName: v.pipe(v.string(), v.nonEmpty('First name is required')),
  lastName: v.pipe(v.string(), v.nonEmpty('Last name is required')),
  email: v.pipe(v.string(), v.nonEmpty('Email is required'), v.email('Enter a valid email')),
  phone: v.optional(v.pipe(v.string(), v.regex(/^\+?[\d\s-()]*$/, 'Enter a valid phone number'))),
  message: v.pipe(
    v.string(),
    v.nonEmpty('Message is required'),
    v.minLength(10, 'Message must be at least 10 characters')
  ),
});

type ContactData = v.InferOutput<typeof contactSchema>;

/**
 * Bridge function: Valibot schema → Form @validate callback.
 * This pattern keeps tio-reimagined zero-dependency — the consumer
 * brings their own validation library and wires it here.
 */
const validateContact = (data: ContactData) => {
  const result = v.safeParse(contactSchema, data);
  if (result.success) return {};
  return v.flatten(result.issues).nested ?? {};
};

// ---------------------------------------------------------------------------
// Forms Demo Page
// ---------------------------------------------------------------------------

export default class FormsDetailPage extends Component {
  // ----- Input demos -----
  @tracked basicInput = '';
  @tracked emailInput = '';
  @tracked passwordInput = '';
  @tracked phoneInput = '';
  @tracked numberInput = '';
  @tracked disabledInput = 'Cannot edit this';
  @tracked inputWithErrors = '';

  setBasicInput = (val: string) => {
    this.basicInput = val;
  };
  setEmailInput = (val: string) => {
    this.emailInput = val;
  };
  setPasswordInput = (val: string) => {
    this.passwordInput = val;
  };
  setPhoneInput = (val: string) => {
    this.phoneInput = val;
  };
  setNumberInput = (val: string) => {
    this.numberInput = val;
  };
  setInputWithErrors = (val: string) => {
    this.inputWithErrors = val;
  };

  get inputErrors(): string[] {
    if (!this.inputWithErrors) return ['This field is required'];
    if (this.inputWithErrors.length < 3) return ['Must be at least 3 characters'];
    return [];
  }

  // ----- Textarea demos -----
  @tracked basicTextarea = '';
  @tracked autoResizeTextarea = '';
  @tracked textareaWithCount = '';

  setBasicTextarea = (val: string) => {
    this.basicTextarea = val;
  };
  setAutoResizeTextarea = (val: string) => {
    this.autoResizeTextarea = val;
  };
  setTextareaWithCount = (val: string) => {
    this.textareaWithCount = val;
  };

  // ----- Checkbox demos -----
  @tracked agreeTerms = false;
  @tracked enableNotifications = true;
  @tracked selectAll = false;
  @tracked checkEmail = true;
  @tracked checkSms = false;
  @tracked checkPush = true;

  setAgreeTerms = (val: boolean) => {
    this.agreeTerms = val;
  };
  setEnableNotifications = (val: boolean) => {
    this.enableNotifications = val;
  };
  setSelectAll = (val: boolean) => {
    this.selectAll = val;
    this.checkEmail = val;
    this.checkSms = val;
    this.checkPush = val;
  };
  setCheckEmail = (val: boolean) => {
    this.checkEmail = val;
    this.updateSelectAll();
  };
  setCheckSms = (val: boolean) => {
    this.checkSms = val;
    this.updateSelectAll();
  };
  setCheckPush = (val: boolean) => {
    this.checkPush = val;
    this.updateSelectAll();
  };

  updateSelectAll = () => {
    const all = this.checkEmail && this.checkSms && this.checkPush;
    const none = !this.checkEmail && !this.checkSms && !this.checkPush;
    this.selectAll = all;
    // Indeterminate handled in template
    this._isIndeterminate = !all && !none;
  };

  @tracked _isIndeterminate = false;

  // ----- RadioGroup demos -----
  @tracked selectedFrequency = 'weekly';
  @tracked selectedPriority = '';

  setFrequency = (val: string) => {
    this.selectedFrequency = val;
  };
  setPriority = (val: string) => {
    this.selectedPriority = val;
  };

  // ----- Switch demos -----
  @tracked darkMode = false;
  @tracked autoSave = true;
  @tracked twoFactor = false;

  setDarkMode = (val: boolean) => {
    this.darkMode = val;
  };
  setAutoSave = (val: boolean) => {
    this.autoSave = val;
  };
  setTwoFactor = (val: boolean) => {
    this.twoFactor = val;
  };

  // ----- SearchInput demos -----
  @tracked searchQuery = '';
  @tracked searchResults = '';

  setSearchQuery = (val: string) => {
    this.searchQuery = val;
  };
  handleSearch = (val: string) => {
    this.searchResults = `Searching for "${val}"…`;
  };
  clearSearch = () => {
    this.searchQuery = '';
    this.searchResults = '';
  };

  // ----- Form demo (Valibot) -----
  @tracked contactData: ContactData = {
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    message: '',
  };

  @tracked formSubmitResult = '';

  handleFormSubmit = () => {
    this.formSubmitResult = `✓ Submitted: ${this.contactData.firstName} ${this.contactData.lastName} <${this.contactData.email}>`;
  };

  handleInvalidSubmit = () => {
    this.formSubmitResult = '✗ Form has validation errors — please fix them.';
  };

  <template>
    {{! template-lint-disable no-bare-strings }}
    <VStack class="w-full min-w-0">
      {{! Back navigation }}
      <div class="mb-4">
        <LinkTo
          @route="authenticated.reimagined.components"
          class="tio-inline-row gap-2 tio-fg-brand dark:text-violet-400 hover:tio-fg-brand dark:hover:text-violet-300 font-medium"
        >
          <PhArrowLeft @size={{18}} />
          Back to Components
        </LinkTo>
      </div>

      {{! Header }}
      <PageHero
        @title="Form Components"
        @subtitle="Zero-dependency form inputs. Every component works standalone or inside a Form wrapper with Valibot validation."
        @icon={{PhTextbox}}
        @gradient="brand"
        @decoration="dots"
      >
        <:actions>
          <Chip @intent="primary">
            8 Components
          </Chip>
          <Chip @intent="success">
            Valibot Integration
          </Chip>
          <Chip @intent="warning">
            Accessible
          </Chip>
        </:actions>
      </PageHero>

      <div class="grid grid-cols-1 lg:grid-cols-2 gap-8">

        {{! ====== Demo 1: Input variants ====== }}
        <div class="tio-bg-surface rounded-xl shadow-sm ring-1 ring-gray-950/4 p-6 space-y-5">
          <SectionHeader
            @icon={{PhTextAa}}
            @title="Input"
            @subtitle="Text, email, password, tel, number — with icons and errors"
            @iconClass="tio-fg-info"
          />

          <Input
            data-test-id="full-name-input"
            @label="Full Name"
            @value={{this.basicInput}}
            @onChange={{this.setBasicInput}}
            @placeholder="Enter your name"
            @leadingIcon={{PhUser}}
          />
          <div class="text-xs tio-fg-muted font-mono">Value: "{{this.basicInput}}"</div>

          <Input
            data-test-id="email-input"
            @label="Email"
            @type="email"
            @value={{this.emailInput}}
            @onChange={{this.setEmailInput}}
            @placeholder="you@example.com"
            @leadingIcon={{PhEnvelope}}
            @isRequired={{true}}
          />

          <Input
            data-test-id="password-input"
            @label="Password"
            @type="password"
            @value={{this.passwordInput}}
            @onChange={{this.setPasswordInput}}
            @placeholder="Enter password"
            @leadingIcon={{PhLock}}
            @hint="Must be at least 8 characters"
          />

          <Input
            data-test-id="phone-input"
            @label="Phone"
            @type="tel"
            @value={{this.phoneInput}}
            @onChange={{this.setPhoneInput}}
            @placeholder="+1 (555) 000-0000"
            @leadingIcon={{PhPhone}}
          />

          <Input
            @label="Disabled Input"
            @value={{this.disabledInput}}
            @isDisabled={{true}}
            data-test-id="disabled-input"
          />

          <Input
            data-test-id="input-with-errors"
            @label="Input with Errors"
            @value={{this.inputWithErrors}}
            @onChange={{this.setInputWithErrors}}
            @placeholder="Type something"
            @errors={{this.inputErrors}}
            @isRequired={{true}}
          />
        </div>

        {{! ====== Demo 2: Input sizes ====== }}
        <div class="tio-bg-surface rounded-xl shadow-sm ring-1 ring-gray-950/4 p-6 space-y-5">
          <SectionHeader
            @icon={{PhTextAa}}
            @title="Input Sizes"
            @subtitle="Small, medium, and large variants"
            @iconClass="tio-fg-brand-accent"
          />

          <Input
            data-test-id="small-input"
            @label="Small"
            @value={{this.basicInput}}
            @onChange={{this.setBasicInput}}
            @placeholder="Small input"
            @size="sm"
          />
          <Input
            data-test-id="medium-input"
            @label="Medium (default)"
            @value={{this.basicInput}}
            @onChange={{this.setBasicInput}}
            @placeholder="Medium input"
            @size="md"
          />
          <Input
            data-test-id="large-input"
            @label="Large"
            @value={{this.basicInput}}
            @onChange={{this.setBasicInput}}
            @placeholder="Large input"
            @size="lg"
          />

          <Input
            data-test-id="number-input"
            @label="Number Input"
            @type="number"
            @value={{this.numberInput}}
            @onChange={{this.setNumberInput}}
            @placeholder="0"
            @hint="Only numeric values"
          />

          <Input
            data-test-id="readonly-input"
            @label="Readonly Input"
            @value="This value cannot be changed"
            @isReadonly={{true}}
          />
        </div>

        {{! ====== Demo 3: Textarea ====== }}
        <div class="tio-bg-surface rounded-xl shadow-sm ring-1 ring-gray-950/4 p-6 space-y-5">
          <SectionHeader
            @icon={{PhClipboardText}}
            @title="Textarea"
            @subtitle="Multiline text input with auto-resize and character count"
            @iconClass="tio-fg-success"
          />

          <Textarea
            data-test-id="description-textarea"
            @label="Description"
            @value={{this.basicTextarea}}
            @onChange={{this.setBasicTextarea}}
            @placeholder="Enter a description…"
            @rows={{3}}
          />
          <div class="text-xs tio-fg-muted font-mono">Length: {{this.basicTextarea.length}}</div>

          <Textarea
            data-test-id="auto-resize-textarea"
            @label="Auto-resize Textarea"
            @value={{this.autoResizeTextarea}}
            @onChange={{this.setAutoResizeTextarea}}
            @placeholder="This textarea grows as you type…"
            @autoResize={{true}}
            @rows={{2}}
            @hint="Try typing multiple lines"
          />

          <Textarea
            data-test-id="max-length-textarea"
            @label="With max length"
            @value={{this.textareaWithCount}}
            @onChange={{this.setTextareaWithCount}}
            @placeholder="Max 200 characters"
            @maxlength={{200}}
            @isRequired={{true}}
          />
          <div class="text-xs tio-fg-muted font-mono">{{this.textareaWithCount.length}} / 200</div>
        </div>

        {{! ====== Demo 4: Checkbox ====== }}
        <div class="tio-bg-surface rounded-xl shadow-sm ring-1 ring-gray-950/4 p-6 space-y-5">
          <SectionHeader
            @icon={{PhCheckSquare}}
            @title="Checkbox"
            @subtitle="Single checkbox and checkbox groups with indeterminate state"
            @iconClass="text-fuchsia-500"
          />

          <Checkbox
            data-test-id="agree-terms-checkbox"
            @label="I agree to the terms and conditions"
            @checked={{this.agreeTerms}}
            @onChange={{this.setAgreeTerms}}
          />

          <Checkbox
            data-test-id="enable-notifications-checkbox"
            @label="Enable notifications"
            @checked={{this.enableNotifications}}
            @onChange={{this.setEnableNotifications}}
            @hint="We'll send you updates about your account"
          />

          <hr class="tio-border-subtle" />

          <Heading @level={{5}} class="tio-fg-secondary">Indeterminate + Group</Heading>

          <Checkbox
            data-test-id="select-all-checkbox"
            @label="Select all channels"
            @checked={{this.selectAll}}
            @onChange={{this.setSelectAll}}
            @isIndeterminate={{this._isIndeterminate}}
          />

          <CheckboxGroup
            data-test-id="notification-channels-checkbox-group"
            @label="Notification Channels"
            @orientation="vertical"
          >
            <Checkbox
              data-test-id="email-checkbox"
              @label="Email"
              @checked={{this.checkEmail}}
              @onChange={{this.setCheckEmail}}
            />
            <Checkbox
              data-test-id="sms-checkbox"
              @label="SMS"
              @checked={{this.checkSms}}
              @onChange={{this.setCheckSms}}
            />
            <Checkbox
              data-test-id="push-checkbox"
              @label="Push"
              @checked={{this.checkPush}}
              @onChange={{this.setCheckPush}}
            />
          </CheckboxGroup>

          <CheckboxGroup
            data-test-id="horizontal-checkbox-group"
            @label="Horizontal layout"
            @orientation="horizontal"
          >
            <Checkbox
              data-test-id="option-a-checkbox"
              @label="Option A"
              @checked={{true}}
              @size="sm"
            />
            <Checkbox
              data-test-id="option-b-checkbox"
              @label="Option B"
              @checked={{false}}
              @size="sm"
            />
            <Checkbox
              data-test-id="option-c-checkbox"
              @label="Option C"
              @checked={{true}}
              @size="sm"
            />
          </CheckboxGroup>

          <div class="text-xs tio-fg-muted font-mono">
            Email:
            {{if this.checkEmail "✓" "✗"}}
            | SMS:
            {{if this.checkSms "✓" "✗"}}
            | Push:
            {{if this.checkPush "✓" "✗"}}
          </div>
        </div>

        {{! ====== Demo 5: RadioGroup ====== }}
        <div class="tio-bg-surface rounded-xl shadow-sm ring-1 ring-gray-950/4 p-6 space-y-5">
          <SectionHeader
            @icon={{PhRadioButton}}
            @title="RadioGroup"
            @subtitle="Radio buttons with descriptions, horizontal layout, and sizes"
            @iconClass="tio-fg-warning"
          />

          <RadioGroup
            data-test-id="frequency-radio-group"
            @label="Notification Frequency"
            @options={{FREQUENCY_OPTIONS}}
            @value={{this.selectedFrequency}}
            @onChange={{this.setFrequency}}
            @isRequired={{true}}
            @hint="Choose how often to receive updates"
          />
          <div class="text-xs tio-fg-muted font-mono">Selected: {{this.selectedFrequency}}</div>

          <hr class="tio-border-subtle" />

          <RadioGroup
            data-test-id="priority-horizontal-radio-group"
            @label="Priority (horizontal)"
            @options={{PRIORITY_OPTIONS}}
            @value={{this.selectedPriority}}
            @onChange={{this.setPriority}}
            @orientation="horizontal"
          />
          <div class="text-xs tio-fg-muted font-mono">
            Selected:
            {{if this.selectedPriority this.selectedPriority "none"}}
          </div>

          <RadioGroup
            data-test-id="priority-small-radio-group"
            @label="Small size"
            @options={{PRIORITY_OPTIONS}}
            @value={{this.selectedPriority}}
            @onChange={{this.setPriority}}
            @size="sm"
          />
        </div>

        {{! ====== Demo 6: Switch ====== }}
        <div class="tio-bg-surface rounded-xl shadow-sm ring-1 ring-gray-950/4 p-6 space-y-5">
          <SectionHeader
            @icon={{PhToggleLeft}}
            @title="Switch"
            @subtitle="Toggle switches with labels, hints, and sizes"
            @iconClass="text-cyan-500"
          />

          <Switch
            data-test-id="dark-mode-switch"
            @label="Dark Mode"
            @checked={{this.darkMode}}
            @onChange={{this.setDarkMode}}
          />

          <Switch
            data-test-id="auto-save-switch"
            @label="Auto-save"
            @checked={{this.autoSave}}
            @onChange={{this.setAutoSave}}
            @hint="Automatically save your work every 30 seconds"
          />

          <Switch
            data-test-id="two-factor-switch"
            @label="Two-factor Authentication"
            @checked={{this.twoFactor}}
            @onChange={{this.setTwoFactor}}
          />

          <Switch
            data-test-id="disabled-switch"
            @label="Disabled switch"
            @checked={{true}}
            @isDisabled={{true}}
          />

          <hr class="tio-border-subtle" />

          <Heading @level={{5}} class="tio-fg-secondary">Size Variants</Heading>
          <div class="tio-row-extra-loose">
            <Switch data-test-id="small-switch" @label="Small" @checked={{true}} @size="sm" />
            <Switch data-test-id="medium-switch" @label="Medium" @checked={{true}} @size="md" />
            <Switch data-test-id="large-switch" @label="Large" @checked={{true}} @size="lg" />
          </div>

          <div class="text-xs tio-fg-muted font-mono">
            Dark:
            {{if this.darkMode "ON" "OFF"}}
            | Auto-save:
            {{if this.autoSave "ON" "OFF"}}
            | 2FA:
            {{if this.twoFactor "ON" "OFF"}}
          </div>
        </div>

        {{! ====== Demo 7: SearchInput ====== }}
        <div class="tio-bg-surface rounded-xl shadow-sm ring-1 ring-gray-950/4 p-6 space-y-5">
          <SectionHeader
            @icon={{PhMagnifyingGlass}}
            @title="SearchInput"
            @subtitle="Search-specific input with clear button and Enter handler"
            @iconClass="text-rose-500"
          />

          <SearchInput
            data-test-id="search-employees-input"
            @label="Search Employees"
            @value={{this.searchQuery}}
            @onChange={{this.setSearchQuery}}
            @onSearch={{this.handleSearch}}
            @onClear={{this.clearSearch}}
            @placeholder="Search by name or email…"
          />
          <div class="text-xs tio-fg-muted font-mono">
            {{if this.searchResults this.searchResults "Press Enter to search"}}
          </div>

          <SearchInput
            data-test-id="small-search-input"
            @label="Small search"
            @value={{this.searchQuery}}
            @onChange={{this.setSearchQuery}}
            @onSearch={{this.handleSearch}}
            @onClear={{this.clearSearch}}
            @placeholder="Compact search…"
            @size="sm"
          />

          <SearchInput
            data-test-id="large-search-input"
            @label="Large search"
            @value={{this.searchQuery}}
            @onChange={{this.setSearchQuery}}
            @onSearch={{this.handleSearch}}
            @onClear={{this.clearSearch}}
            @placeholder="Big search…"
            @size="lg"
          />
        </div>

        {{! ====== Demo 8: Form with Valibot ====== }}
        <div
          class="tio-bg-surface rounded-xl shadow-sm ring-1 ring-gray-950/4 p-6 space-y-5 lg:col-span-2"
        >
          <SectionHeader
            @icon={{PhClipboardText}}
            @title="Form with Valibot Validation"
            @subtitle="Orchestrated form with schema-driven validation, touched tracking, and submit handling"
            @iconClass="tio-fg-brand-accent"
          />

          <Form
            @data={{this.contactData}}
            @validate={{validateContact}}
            @onSubmit={{this.handleFormSubmit}}
            @onInvalidSubmit={{this.handleInvalidSubmit}}
            class="space-y-4"
            as |form|
          >
            <div class="grid grid-cols-1 sm:grid-cols-2 gap-4">
              {{#let (form.field "firstName") as |f|}}
                <Input
                  data-test-id="form-first-name-input"
                  @label="First Name"
                  @value={{f.value}}
                  @onChange={{f.onChange}}
                  @onBlur={{f.onBlur}}
                  @errors={{f.errors}}
                  @isRequired={{true}}
                  @placeholder="John"
                  @leadingIcon={{PhUser}}
                />
              {{/let}}

              {{#let (form.field "lastName") as |f|}}
                <Input
                  data-test-id="form-last-name-input"
                  @label="Last Name"
                  @value={{f.value}}
                  @onChange={{f.onChange}}
                  @onBlur={{f.onBlur}}
                  @errors={{f.errors}}
                  @isRequired={{true}}
                  @placeholder="Doe"
                  @leadingIcon={{PhUser}}
                />
              {{/let}}
            </div>

            {{#let (form.field "email") as |f|}}
              <Input
                data-test-id="form-email-input"
                @label="Email"
                @type="email"
                @value={{f.value}}
                @onChange={{f.onChange}}
                @onBlur={{f.onBlur}}
                @errors={{f.errors}}
                @isRequired={{true}}
                @placeholder="john@example.com"
                @leadingIcon={{PhEnvelope}}
              />
            {{/let}}

            {{#let (form.field "phone") as |f|}}
              <Input
                data-test-id="form-phone-input"
                @label="Phone (optional)"
                @type="tel"
                @value={{f.value}}
                @onChange={{f.onChange}}
                @onBlur={{f.onBlur}}
                @errors={{f.errors}}
                @placeholder="+1 (555) 000-0000"
                @leadingIcon={{PhPhone}}
              />
            {{/let}}

            {{#let (form.field "message") as |f|}}
              <Textarea
                @label="Message"
                @value={{f.value}}
                @onChange={{f.onChange}}
                @onBlur={{f.onBlur}}
                @errors={{f.errors}}
                @isRequired={{true}}
                @placeholder="Tell us how we can help…"
                @rows={{4}}
                @autoResize={{true}}
              />
            {{/let}}

            <div class="tio-row-loose pt-2">
              <Button type="submit" @intent="primary">
                Submit Form
              </Button>
              <Button type="button" @intent="default" {{on "click" form.reset}}>
                Reset
              </Button>
              <div class="tio-row text-xs tio-fg-muted font-mono">
                <span>Valid: {{if form.isValid "✓" "✗"}}</span>
                <span>Dirty: {{if form.isDirty "✓" "✗"}}</span>
              </div>
            </div>
          </Form>

          {{#if this.formSubmitResult}}
            <div class="mt-4 p-3 rounded-lg tio-bg-sunken text-sm font-mono tio-fg-secondary">
              {{this.formSubmitResult}}
            </div>
          {{/if}}
        </div>

      </div>

      {{! ================================================================
            Summary
            ================================================================ }}
      <div
        class="tio-bg-brand-wash dark:bg-violet-950 rounded-xl p-6 border border-violet-100 dark:border-violet-800"
      >
        <div class="tio-row-top-loose">
          <div class="w-12 h-12 rounded-full tio-bg-brand-soft tio-row-center shrink-0">
            <PhTextbox @size={{24}} class="tio-fg-brand" />
          </div>
          <div>
            <Heading @level={{3}} class="tio-fg-brand dark:text-violet-100 mb-2">Form Components
              Summary</Heading>
            <Text @variant="muted" class="mb-4">
              8 form components in tio-reimagined. Every component works standalone with controlled
              values (@value + @onChange). The Form orchestrator adds Valibot schema validation,
              touched tracking, and error distribution.
            </Text>
            <div class="tio-wrap-tight">
              <Chip @intent="primary" @size="sm">Input</Chip>
              <Chip @intent="primary" @size="sm">Textarea</Chip>
              <Chip @intent="primary" @size="sm">Checkbox</Chip>
              <Chip @intent="primary" @size="sm">CheckboxGroup</Chip>
              <Chip @intent="primary" @size="sm">RadioGroup</Chip>
              <Chip @intent="primary" @size="sm">Switch</Chip>
              <Chip @intent="primary" @size="sm">SearchInput</Chip>
              <Chip @intent="primary" @size="sm">Form ✦</Chip>
            </div>
          </div>
        </div>
      </div>
    </VStack>
  </template>
}
